import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { Input, StackField } from "@/components/ui/form";
import { MoreLink } from "@/components/ui/more-link";
import { Notice } from "@/components/ui/notice";
import { authClient, authErrorMessage } from "@/lib/auth-client";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` });
    setLoading(false);
    // Same answer whether or not the address exists, so accounts cannot be probed.
    if (error && error.status !== 404) return setError(authErrorMessage(error));
    setSent(true);
  };

  return (
    <AuthLayout title="パスワードの再設定" lead="ご登録のメールアドレスに、再設定用のリンクをお送りします。">
      {sent ? (
        <Notice tone="ok" title="メールを送信しました">登録されているアドレスであれば、数分以内にメールが届きます。届かない場合は迷惑メールフォルダもご確認ください。</Notice>
      ) : (
        <form noValidate onSubmit={submit} className="space-y-4">
          {error && <Notice tone="bad">{error}</Notice>}
          <StackField label="メールアドレス">{(id) => <Input id={id} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />}</StackField>
          <Button type="submit" size="lg" className="w-full" loading={loading} disabled={!email}>送信する</Button>
        </form>
      )}
      <p className="mt-4 text-center"><MoreLink to="/login">ログイン画面へ戻る</MoreLink></p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(params.get("error") ? "リンクの有効期限が切れています。もう一度お手続きください" : null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 12) return setError("パスワードは12文字以上にしてください");
    if (password !== confirm) return setError("確認用のパスワードが一致しません");
    setLoading(true);
    const { error } = await authClient.resetPassword({ newPassword: password, token });
    setLoading(false);
    if (error) return setError(authErrorMessage(error));
    navigate("/login?reset=1", { replace: true });
  };

  return (
    <AuthLayout title="新しいパスワードの設定">
      <form noValidate onSubmit={submit} className="space-y-4">
        {error && <Notice tone="bad">{error}</Notice>}
        <StackField label="新しいパスワード" required hint="12文字以上">{(id) => <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</StackField>
        <StackField label="新しいパスワード（確認）" required>{(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}</StackField>
        <Button type="submit" size="lg" className="w-full" loading={loading} disabled={!token}>変更する</Button>
      </form>
    </AuthLayout>
  );
}
